import { tasks } from "@history-sim/content";
import { useCallback, useEffect } from "react";
import { useAppStore } from "../state/appStore";
import type { FlushResult } from "./learnerSyncCoordinator";
import {
  getLearnerSyncRuntime,
  isLocalOnlyLearnerSession,
  type LearnerSyncRuntime,
  learnerSyncConfiguration,
} from "./learnerSyncRuntime";
import { useLearnerSyncStatus } from "./syncStatus";
import {
  TASK_ATTEMPT_QUEUED_EVENT,
  TASK_ATTEMPT_REJECTED_EVENT,
  TASK_ATTEMPT_RESOLVED_EVENT,
  type TaskAttemptRejectedEventDetail,
  type TaskAttemptResolvedEventDetail,
  taskAttemptResponseToOutcome,
} from "./taskAdjudication";

const EVENT_SYNC_DEBOUNCE_MS = 500;
const TASK_ATTEMPT_RETRY_MS = 15_000;

function publishFlushResult(result: FlushResult): void {
  const status = useLearnerSyncStatus.getState();
  if (result.status === "idle" || result.status === "synced") {
    status.setStatus(result.pendingEvents > 0 ? "pending" : "synced", {
      pendingEvents: result.pendingEvents,
    });
    return;
  }
  status.setStatus(result.status, {
    pendingEvents: result.pendingEvents,
    reasonCode: "reasonCode" in result ? result.reasonCode : "SYNC_RUNTIME_FAILED",
  });
}

interface LearnerSyncBridgeProps {
  readonly runtime?: LearnerSyncRuntime;
}

export function LearnerSyncBridge({ runtime: providedRuntime }: LearnerSyncBridgeProps = {}) {
  const sessionId = useAppStore((state) => state.sessionId);
  const events = useAppStore((state) => state.events);

  const synchronize = useCallback(async () => {
    const runtime = providedRuntime ?? getLearnerSyncRuntime();
    if (!runtime) return;
    if (isLocalOnlyLearnerSession(sessionId)) {
      useLearnerSyncStatus.getState().setStatus("local-only");
      return;
    }
    try {
      const mapping = await runtime.outbox.getSession(sessionId);
      if (!mapping) {
        useLearnerSyncStatus.getState().setStatus("not-connected");
        return;
      }

      await runtime.coordinator.enqueue(sessionId, events);
      const pendingEvents = await runtime.outbox.pendingCount(sessionId);
      const pendingAttempts = await runtime.outbox.pendingTaskAttemptCount(sessionId);
      if (!navigator.onLine) {
        useLearnerSyncStatus
          .getState()
          .setStatus("offline", { pendingEvents: pendingEvents + pendingAttempts });
        return;
      }
      if (pendingEvents + pendingAttempts > 0) {
        useLearnerSyncStatus
          .getState()
          .setStatus("pending", { pendingEvents: pendingEvents + pendingAttempts });
      }
      const taskResult = await runtime.coordinator.flushTaskAttempts(sessionId);
      for (const receipt of taskResult.delivered) {
        const detail: TaskAttemptResolvedEventDetail = {
          clientAttemptId: receipt.clientAttemptId,
          outcome: taskAttemptResponseToOutcome(receipt.response),
        };
        const handledByActiveTask = !window.dispatchEvent(
          new CustomEvent<TaskAttemptResolvedEventDetail>(TASK_ATTEMPT_RESOLVED_EVENT, {
            detail,
            cancelable: true,
          }),
        );
        let receiptApplied = handledByActiveTask;
        if (!handledByActiveTask) {
          const task = tasks.find((candidate) => candidate.id === receipt.response.taskId);
          if (task) {
            useAppStore.getState().applyServerTaskAttempt(task.sceneId, receipt.response);
            receiptApplied = true;
          }
        }
        if (receiptApplied) await runtime.outbox.markTaskAttemptApplied(receipt.clientAttemptId);
      }
      for (const rejected of taskResult.rejected) {
        const detail: TaskAttemptRejectedEventDetail = rejected;
        window.dispatchEvent(
          new CustomEvent<TaskAttemptRejectedEventDetail>(TASK_ATTEMPT_REJECTED_EVENT, {
            detail,
            cancelable: true,
          }),
        );
      }

      const eventResult = await runtime.coordinator.flush(sessionId);
      if (
        taskResult.status === "auth-required" ||
        taskResult.status === "blocked" ||
        taskResult.status === "retry"
      ) {
        useLearnerSyncStatus.getState().setStatus(taskResult.status, {
          pendingEvents: taskResult.pendingAttempts + eventResult.pendingEvents,
          reasonCode: taskResult.reasonCode ?? "TASK_ATTEMPT_SYNC_FAILED",
        });
        return;
      }
      publishFlushResult({
        ...eventResult,
        pendingEvents: eventResult.pendingEvents + taskResult.pendingAttempts,
      });
    } catch (error) {
      useLearnerSyncStatus.getState().setStatus("error", {
        reasonCode:
          error && typeof error === "object" && "code" in error && typeof error.code === "string"
            ? error.code
            : "SYNC_RUNTIME_FAILED",
      });
    }
  }, [events, providedRuntime, sessionId]);

  useEffect(() => {
    if (!providedRuntime && learnerSyncConfiguration.mode === "local-only") {
      useLearnerSyncStatus.getState().setStatus("local-only");
      return;
    }
    const timer = window.setTimeout(() => void synchronize(), EVENT_SYNC_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [providedRuntime, synchronize]);

  useEffect(() => {
    if (!providedRuntime && learnerSyncConfiguration.mode === "local-only") return;
    const synchronizeWhenOnline = () => void synchronize();
    let queuedAttemptTimer: number | undefined;
    const synchronizeQueuedAttempt = () => {
      if (queuedAttemptTimer !== undefined) window.clearTimeout(queuedAttemptTimer);
      queuedAttemptTimer = window.setTimeout(() => void synchronize(), EVENT_SYNC_DEBOUNCE_MS);
    };
    const synchronizeWhenVisible = () => {
      if (document.visibilityState === "visible") void synchronize();
    };
    window.addEventListener("online", synchronizeWhenOnline);
    window.addEventListener(TASK_ATTEMPT_QUEUED_EVENT, synchronizeQueuedAttempt);
    document.addEventListener("visibilitychange", synchronizeWhenVisible);
    const retryTimer = window.setInterval(() => void synchronize(), TASK_ATTEMPT_RETRY_MS);
    return () => {
      window.removeEventListener("online", synchronizeWhenOnline);
      window.removeEventListener(TASK_ATTEMPT_QUEUED_EVENT, synchronizeQueuedAttempt);
      document.removeEventListener("visibilitychange", synchronizeWhenVisible);
      if (queuedAttemptTimer !== undefined) window.clearTimeout(queuedAttemptTimer);
      window.clearInterval(retryTimer);
    };
  }, [providedRuntime, synchronize]);

  return null;
}
